import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer } from '../../components/layout';
import { KpiCard, FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import {
  adminMiradorService,
  type MiradorClientItem,
  type HealthResult,
  type OutboxStats,
  type OutboxEntry,
} from '../../services/adminMiradorService';
import { toastError, toastSuccess } from '../../lib/toast';
import { ClientHealthCard } from './ClientHealthCard';
import { SchemaCoverageSection } from './SchemaCoverageSection';

const POLL_MS = 30_000;

export const MiradorBridgePage: React.FC = () => {
  const [tenants, setTenants] = useState<MiradorClientItem[]>([]);
  const [health, setHealth] = useState<Record<string, HealthResult | undefined>>({});
  const [stats, setStats] = useState<OutboxStats | null>(null);
  const [failures, setFailures] = useState<OutboxEntry[]>([]);
  const [loadingFailures, setLoadingFailures] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const loadTenants = useCallback(async () => {
    try {
      const t = await adminMiradorService.listClients();
      setTenants(t);
    } catch (err) {
      toastError('Chargement des tenants Mirador échoué', err);
    }
  }, []);

  const refreshHealth = useCallback(async () => {
    const results: Record<string, HealthResult | undefined> = {};
    await Promise.all(
      tenants.map(async (t) => {
        try {
          results[t.client_id] = await adminMiradorService.health(t.client_id);
        } catch {
          results[t.client_id] = undefined;
        }
      }),
    );
    setHealth(results);
  }, [tenants]);

  const refreshStats = useCallback(async () => {
    try {
      const s = await adminMiradorService.outboxStats();
      setStats(s);
    } catch {
      /* silent */
    }
  }, []);

  const refreshFailures = useCallback(async () => {
    setLoadingFailures(true);
    try {
      const items = await adminMiradorService.listOutbox({ status: 'failed', limit: 50 });
      setFailures(items);
    } catch (err) {
      toastError('Chargement des échecs échoué', err);
    } finally {
      setLoadingFailures(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    if (tenants.length === 0) return;
    refreshHealth();
    const id = window.setInterval(refreshHealth, POLL_MS);
    return () => window.clearInterval(id);
  }, [tenants, refreshHealth]);

  useEffect(() => {
    refreshStats();
    refreshFailures();
    const id = window.setInterval(() => {
      refreshStats();
      refreshFailures();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [refreshStats, refreshFailures]);

  const handleRetry = async (id: string) => {
    setRetryingId(id);
    try {
      await adminMiradorService.retryOutbox(id);
      toastSuccess('Entrée rejouée');
      await refreshFailures();
      await refreshStats();
    } catch (err) {
      toastError('Retry échoué', err);
    } finally {
      setRetryingId(null);
    }
  };

  const columns: FilterableColumnDef<OutboxEntry>[] = useMemo(
    () => [
      {
        key: 'created_at',
        header: 'Date',
        render: (r) => new Date(r.created_at).toLocaleString('fr-CA'),
        sortable: true,
        sortValueFn: (r) => r.created_at,
        width: '170px',
      },
      {
        key: 'client_slug',
        header: 'Tenant',
        render: (r) => r.client_slug ?? '—',
        filterable: true,
        filterValueFn: (r) => r.client_slug ?? '',
        width: '140px',
      },
      {
        key: 'event_type',
        header: 'Event',
        render: (r) => r.event_type,
        filterable: true,
        filterValueFn: (r) => r.event_type,
        width: '140px',
      },
      {
        key: 'document_id',
        header: 'Document',
        render: (r) => (
          <Link
            to={`/extraction/documents/${r.document_id}`}
            className="text-blue-600 hover:underline font-mono text-xs"
          >
            {r.document_id.slice(0, 8)}…
          </Link>
        ),
        width: '120px',
      },
      {
        key: 'attempt_count',
        header: 'Tentatives',
        render: (r) => r.attempt_count,
        sortable: true,
        sortValueFn: (r) => String(r.attempt_count).padStart(3, '0'),
        align: 'center',
        width: '90px',
      },
      {
        key: 'last_error',
        header: 'Erreur',
        render: (r) => (
          <span
            className="block max-w-[420px] truncate text-xs text-red-700"
            title={r.last_error ?? ''}
          >
            {r.last_error ?? '—'}
          </span>
        ),
      },
      {
        key: 'action',
        header: '',
        render: (r) => (
          <button
            type="button"
            onClick={() => handleRetry(r.id)}
            disabled={retryingId === r.id}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {retryingId === r.id ? '…' : 'Rejouer'}
          </button>
        ),
        align: 'right',
        width: '90px',
      },
    ],
    [retryingId],
  );

  return (
    <PageContainer>
      <h1 className="text-2xl font-bold mb-2">Pont Mirador</h1>
      <p className="text-sm text-gray-600 mb-6">
        Monitoring du push Dokimo → Mirador (table MySQL <code>Table_DocImmo_Result</code>).
      </p>

      {/* Section 1 — Healthcheck par tenant */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Healthcheck par tenant</h2>
        {tenants.length === 0 ? (
          <div className="text-sm text-gray-500 italic">
            Aucun tenant n'a de configuration Mirador active.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenants.map((t) => (
              <ClientHealthCard key={t.client_id} tenant={t} health={health[t.client_id]} />
            ))}
          </div>
        )}
      </section>

      {/* Section 2 — Compteurs outbox */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Outbox (24h glissantes)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Pending" value={stats?.pending ?? '…'} />
          <KpiCard label="Processing" value={stats?.processing ?? '…'} />
          <KpiCard label="Processed 24h" value={stats?.processed_24h ?? '…'} />
          <KpiCard label="Failed 24h" value={stats?.failed_24h ?? '…'} />
        </div>
      </section>

      {/* Section 3 — 50 derniers échecs */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">50 derniers échecs</h2>
          <button
            type="button"
            onClick={refreshFailures}
            disabled={loadingFailures}
            className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            {loadingFailures ? 'Actualisation…' : 'Actualiser'}
          </button>
        </div>
        <FilterableTable
          data={failures}
          columns={columns}
          rowKey={(r) => r.id}
          emptyMessage="Aucun échec sur les 24 dernières heures."
          noDataMessage="Aucun échec sur les 24 dernières heures."
          defaultSort={{ key: 'created_at', direction: 'desc' }}
        />
      </section>

      {/* Section 4 — Schema coverage */}
      <SchemaCoverageSection tenants={tenants} />
    </PageContainer>
  );
};

export default MiradorBridgePage;
